import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Row, Col, Button, Form, Alert, Modal } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  updateSessionById,
  uploadImage,
  fetchPracticeTemplates,
} from "../../api";
import ReactQuill from "react-quill";
import ImageAnnotator from "./EditImages";
import {
  FaStop,
} from "react-icons/fa";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./EditSession.css";
import "../Communications/Comms.css";

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };
  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };
  const clear = () => resetTranscript();
  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]); // clear transcript on open

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop();
  const handleUse = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop();
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              aria-live="polite"
              style={{
                minHeight: 90,
                border: "1px solid rgba(0,0,0,.1)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "#fafafa",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || (
                <span className="text-muted">Say something…</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="outline-primary" onClick={handleStart}>
              <i className="fa-light fa-microphone me-2"></i> Start
            </Button>
          ) : (
            <Button variant="danger" onClick={handleStop}>
              <FaStop className="me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUse}
            disabled={!vr.transcript?.trim()}
          >
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// --- Handwriting Modal (fullscreen canvas for stylus/touch input) ---
const HandwritingModal = ({ show, onHide, onSave, existingImage }) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Load existing image as background when modal opens
  useEffect(() => {
    if (show && existingImage) {
      setBackgroundImage(existingImage);
    } else if (!show) {
      setBackgroundImage(null);
    }
  }, [show, existingImage]);

  const handleSave = async () => {
    if (canvasRef.current) {
      try {
        // Check if user drew any new strokes
        const paths = await canvasRef.current.exportPaths();
        const hasNewStrokes = paths && paths.length > 0;

        // If there's a background but no new strokes, keep the original unchanged
        if (backgroundImage && !hasNewStrokes) {
          onSave(backgroundImage);
          onHide();
          return;
        }

        // Export the strokes layer at native canvas dimensions
        const strokesDataUrl = await canvasRef.current.exportImage("png");

        // If there's a background image, merge it with the strokes
        if (backgroundImage && hasNewStrokes) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Load strokes image first to get canvas dimensions
          const strokesImg = new Image();
          strokesImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            strokesImg.onload = resolve;
            strokesImg.onerror = reject;
            strokesImg.src = strokesDataUrl;
          });

          // Load background image
          const bgImg = new Image();
          bgImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            bgImg.onload = resolve;
            bgImg.onerror = reject;
            bgImg.src = backgroundImage;
          });

          // Use strokes canvas dimensions as the base
          canvas.width = strokesImg.width || 800;
          canvas.height = strokesImg.height || 600;

          // Calculate aspect-ratio-preserving dimensions for background
          const bgAspect = bgImg.width / bgImg.height;
          const canvasAspect = canvas.width / canvas.height;

          let drawWidth, drawHeight, offsetX, offsetY;

          if (bgAspect > canvasAspect) {
            // Background is wider - fit to width
            drawWidth = canvas.width;
            drawHeight = canvas.width / bgAspect;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Background is taller - fit to height
            drawHeight = canvas.height;
            drawWidth = canvas.height * bgAspect;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }

          // Draw background centered and aspect-ratio preserved (keep transparent)
          ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);

          // Draw strokes on top at their native size (no scaling needed)
          if (strokesImg.width > 0 && strokesImg.height > 0) {
            ctx.drawImage(strokesImg, 0, 0);
          }

          // Export merged image
          const mergedDataUrl = canvas.toDataURL("image/png");
          onSave(mergedDataUrl);
        } else {
          // No background, just use the strokes
          onSave(strokesDataUrl);
        }

        onHide();
      } catch (err) {
        console.error("Failed to export canvas:", err);
      }
    }
  };

  const handleUndo = () => canvasRef.current?.undo();
  const handleRedo = () => canvasRef.current?.redo();
  const handleClear = () => canvasRef.current?.clearCanvas();

  const toggleEraser = () => {
    setIsEraser(!isEraser);
    setIsHighlighter(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(!isEraser);
    }
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    if (canvasRef.current) canvasRef.current.eraseMode(false);
    if (next) {
      setStrokeColor("rgba(255,255,0,0.4)");
      setStrokeWidth(20);
    } else {
      setStrokeColor("#000000");
      setStrokeWidth(3);
    }
  };

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="handwriting-modal"
      backdrop="static"
    >
      <Modal.Header className="handwriting-modal-header py-2" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          {/* Color picker */}
          <div className="d-flex align-items-center gap-1">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setStrokeColor(color);
                  setIsEraser(false);
                  setIsHighlighter(false);
                  setStrokeWidth(3);
                  canvasRef.current?.eraseMode(false);
                }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          {/* Stroke width */}
          <div className="d-flex align-items-center gap-1">
            {strokeOptions.map((width) => (
              <button
                key={width}
                type="button"
                className={`stroke-btn ${strokeWidth === width && !isHighlighter ? "active" : ""}`}
                onClick={() => {
                  setStrokeWidth(width);
                  setIsHighlighter(false);
                }}
                aria-label={`Stroke width ${width}`}
              >
                <span
                  style={{ width: width * 2, height: width * 2 }}
                  className="stroke-preview"
                />
              </button>
            ))}
          </div>

          {/* Tools */}
          <div className="d-flex align-items-center gap-2">
            <Button
              variant={isHighlighter ? "warning" : "outline-primary"}
              size="sm"
              onClick={toggleHighlighter}
              title="Highlighter"
            >
              <i className="fa-solid fa-highlighter" />
            </Button>
            <Button
              variant={isEraser ? "primary" : "outline-primary"}
              size="sm"
              onClick={toggleEraser}
              title="Eraser"
            >
              <i className="fa-solid fa-eraser" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleUndo}
              title="Undo"
            >
              <i className="fa-solid fa-rotate-left" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRedo}
              title="Redo"
            >
              <i className="fa-solid fa-rotate-right" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleClear}
              title="Clear all"
            >
              <i className="fa-solid fa-trash" />
            </Button>
          </div>

          {/* Actions */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button variant="outline-primary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Save Drawing
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0 handwriting-canvas-container" style={{ overflowY: "auto" }}>
        <div style={{ width: "100%", height: "600vh", position: "relative" }}>
          <ReactSketchCanvas
            ref={canvasRef}
            strokeWidth={strokeWidth}
            strokeColor={isEraser ? "transparent" : strokeColor}
            canvasColor="transparent"
            backgroundImage={backgroundImage || ""}
            preserveBackgroundImageAspectRatio="xMidYMid meet"
            style={{
              width: "100%",
              height: "100%",
            }}
            allowOnlyPointerType="all"
            withTimestamp={true}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- Inline Write Section (canvas for handwriting inside the bordered layout) ---
const InlineWriteSection = forwardRef(({ onSave, existingImage, onExpand, isFullScreen }, ref) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    setIsHighlighter(false);
    canvasRef.current?.eraseMode(next);
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    canvasRef.current?.eraseMode(false);
    if (next) { setStrokeColor("rgba(255,255,0,0.4)"); setStrokeWidth(20); }
    else { setStrokeColor("#000000"); setStrokeWidth(3); }
  };

  // Merges current strokes with background and returns the composite image.
  // Does NOT clear the canvas so the user can keep editing after navigating away and back.
  const capture = async () => {
    if (!canvasRef.current) return existingImage;
    try {
      const paths = await canvasRef.current.exportPaths();
      const hasNewStrokes = paths && paths.length > 0;
      if (existingImage && !hasNewStrokes) return existingImage;
      const strokesDataUrl = await canvasRef.current.exportImage("png");
      let result;
      if (existingImage && hasNewStrokes) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const strokesImg = new Image();
        strokesImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { strokesImg.onload = res; strokesImg.onerror = rej; strokesImg.src = strokesDataUrl; });
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { bgImg.onload = res; bgImg.onerror = rej; bgImg.src = existingImage; });
        canvas.width = strokesImg.width || 800;
        canvas.height = strokesImg.height || 600;
        const bgAspect = bgImg.width / bgImg.height;
        const canvasAspect = canvas.width / canvas.height;
        let dw, dh, ox, oy;
        if (bgAspect > canvasAspect) { dw = canvas.width; dh = canvas.width / bgAspect; ox = 0; oy = (canvas.height - dh) / 2; }
        else { dh = canvas.height; dw = canvas.height * bgAspect; ox = (canvas.width - dw) / 2; oy = 0; }
        ctx.drawImage(bgImg, ox, oy, dw, dh);
        if (strokesImg.width > 0) ctx.drawImage(strokesImg, 0, 0);
        result = canvas.toDataURL("image/png");
      } else {
        result = strokesDataUrl;
      }
      onSave(result);
      return result;
    } catch (err) { console.error("Failed to capture canvas:", err); return existingImage; }
  };

  useImperativeHandle(ref, () => ({ capture }));

  return (
    <div>
      {/* Toolbar */}
      <div className="handwriting-modal-header d-flex align-items-center gap-2 flex-wrap p-2 mb-1" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-1">
          {colorOptions.map((color) => (
            <button key={color} type="button"
              className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
              style={{ backgroundColor: color }}
              onClick={() => { setStrokeColor(color); setIsEraser(false); setIsHighlighter(false); setStrokeWidth(3); canvasRef.current?.eraseMode(false); }}
            />
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          {strokeOptions.map((w) => (
            <button key={w} type="button"
              className={`stroke-btn ${strokeWidth === w && !isHighlighter ? "active" : ""}`}
              onClick={() => { setStrokeWidth(w); setIsHighlighter(false); }}
            >
              <span style={{ width: w * 2, height: w * 2 }} className="stroke-preview" />
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          <Button variant={isHighlighter ? "warning" : "outline-primary"} size="sm" onClick={toggleHighlighter} title="Highlighter"><i className="fa-solid fa-highlighter" /></Button>
          <Button variant={isEraser ? "primary" : "outline-primary"} size="sm" onClick={toggleEraser} title="Eraser"><i className="fa-solid fa-eraser" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.undo()} title="Undo"><i className="fa-solid fa-rotate-left" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.redo()} title="Redo"><i className="fa-solid fa-rotate-right" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.clearCanvas()} title="Clear"><i className="fa-solid fa-trash" /></Button>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <Button variant="" className="rounded-0 text-primary" onClick={onExpand} title={isFullScreen ? "Collapse" : "Expand"}>
            <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
          </Button>
        </div>
      </div>
      {/* Canvas */}
      <div className="handwriting-canvas-container" style={{ width: "100%", height: "1200px", overflow: "hidden" }}>
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={isHighlighter ? 20 : strokeWidth}
          strokeColor={isEraser ? "transparent" : (isHighlighter ? "rgba(255,255,0,0.4)" : strokeColor)}
          canvasColor="transparent"
          backgroundImage={existingImage || ""}
          preserveBackgroundImageAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
          allowOnlyPointerType="all"
          withTimestamp={true}
        />
      </div>
    </div>
  );
});

export const UpdateSession = ({
  sessionId,
  patientId,
  appointmentId,
  serviceName,
  appointmentStart,
  appointmentStatus,
  sections = {},
  notepadContentR = "",
  notepadContentType = "",
  handwritingImageR = null,
  templateId,
  practiceId,
  resourceId,
  activeSection,
  onSaved,
  mediaFilesR = [],
  inline = false,
}) => {
  const [notes, setNotes] = useState([]);
  const [notepadContent, setNotepadContent] = useState("");
  const [handwritingImage, setHandwritingImage] = useState(null);
  const [showHandwritingModal, setShowHandwritingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateId || "",
  );
  const quillRef = useRef(null);
  const annotatorRefs = useRef([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [mediaImages, setMediaImages] = useState([]); // strings (dataURL or http)
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const loadedNotepadRef = useRef(false);
  const initialTemplateIdRef = useRef(templateId || "");
  const Navigate = useNavigate();
  const [localSection, setLocalSection] = useState(activeSection || "apptTemplate");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const inlineWriteRef = useRef(null);

  const handleSectionChange = async (key) => {
    // Capture write canvas while still visible before hiding it — exportImage fails on display:none
    if (localSection === "write" && key !== "write" && inlineWriteRef.current) {
      await inlineWriteRef.current.capture();
    }
    setLocalSection(key);
  };

  const handleExpand = async () => {
    // Capture canvas before fullscreen toggle — portaling remounts the tree, which resets the canvas
    if (inlineWriteRef.current) await inlineWriteRef.current.capture();
    setIsFullScreen((f) => !f);
  };

  // Sync localSection from activeSection prop
  useEffect(() => {
    if (activeSection) setLocalSection(activeSection);
  }, [activeSection]);

  const navItems = [
    { key: "apptTemplate", label: "Template", icon: "fa-light fa-file-waveform" },
    { key: "apptNotepad",  label: "Note Pad", icon: "fa-light fa-clipboard-medical" },
    { key: "write",        label: "Write",    icon: "fa-light fa-pen-nib" },
    { key: "apptMedia",    label: "Media",    icon: "fa-light fa-camera" },
  ];

  const safeHtml = (v) => (typeof v === "string" ? v : v ? String(v) : "");

  // shared normalizer
  const normalizeNotes = (incoming) => {
    const arr = Array.isArray(incoming?.sections)
      ? incoming.sections
      : Array.isArray(incoming)
        ? incoming
        : Array.isArray(incoming?.notes)
          ? incoming.notes
          : [];

    return arr.map((n) =>
      n.sessionsFieldType === "multiselect"
        ? {
            ...n,
            sessionsSectionNotes: {
              availableOptions: Array.isArray(
                n.sessionsSectionNotes?.availableOptions,
              )
                ? n.sessionsSectionNotes.availableOptions
                : Array.isArray(n.sessionsSectionNotes)
                  ? n.sessionsSectionNotes
                  : [],
              selectedOptions: Array.isArray(
                n.sessionsSectionNotes?.selectedOptions,
              )
                ? n.sessionsSectionNotes.selectedOptions
                : [],
            },
          }
        : {
            ...n,
            sessionsSectionNotes:
              typeof n.sessionsSectionNotes === "string"
                ? n.sessionsSectionNotes
                : (n.sessionsSectionNotes ?? "") + "",
          },
    );
  };

  // media init/merge
  useEffect(() => {
    const urls = (mediaFilesR || [])
      .map((f) => (typeof f === "string" ? f : f?.fileUrl))
      .filter(Boolean);

    if (!urls.length) return;
    setMediaImages((prev) => {
      if (!prev.length) return urls;
      const seen = new Set(urls);
      return [...urls, ...prev.filter((u) => !seen.has(u))];
    });
  }, [sessionId, mediaFilesR]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamError(null);
    } catch (e) {
      setCamError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (localSection === "apptMedia") startStream();
    else stopStream();
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSection, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) =>
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  // Recorder wiring
  const vr = useVoiceRecorder();
  const [recOpen, setRecOpen] = useState(false);
  const [recTarget, setRecTarget] = useState(null); // { type:'template'|'notepad', index? }

  const openRecorderForTemplate = (index) => {
    setRecTarget({ type: "template", index });
    setRecOpen(true);
  };
  const openRecorderForNotepad = () => {
    setRecTarget({ type: "notepad" });
    setRecOpen(true);
  };
  const closeRecorder = () => {
    if (vr.listening) vr.stop();
    setRecOpen(false);
    setRecTarget(null);
  };

  const insertToQuill = (value) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const end = quill.getLength();
    const needsSpace = end > 1 && /\S/.test(quill.getText(end - 2, 1));
    const text = (needsSpace ? " " : "") + (value || "");
    quill.insertText(end, text, "user");
    quill.setSelection(end + text.length, 0, "user");
    setTimeout(() => setNotepadContent(quill.root.innerHTML), 0);
  };

  const applyTranscriptToTarget = (text) => {
    const cleaned = (text || "").trim();
    if (!cleaned) return;
    if (recTarget?.type === "template" && Number.isInteger(recTarget.index)) {
      setNotes((prev) => {
        const updated = [...prev];
        const idx = recTarget.index;
        const raw = updated[idx]?.sessionsSectionNotes;
        const current = typeof raw === "string" ? raw : (raw ?? "") + "";
        updated[idx] = {
          ...updated[idx],
          sessionsSectionNotes: current ? `${current} ${cleaned}` : cleaned,
        };
        return updated;
      });
    } else if (recTarget?.type === "notepad") {
      insertToQuill(cleaned);
    }
  };

  useEffect(
    () => () => {
      if (vr.listening) vr.stop();
    },
    [vr.listening],
  );

  useEffect(() => {
    annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
  }, [notes.length]);

  const commitAllImages = async () => {
    const copy = [...notes];
    let changed = false;
    for (let idx = 0; idx < copy.length; idx++) {
      const note = copy[idx];
      if (note?.sessionsFieldType !== "image") continue;
      const ref = annotatorRefs.current[idx];
      if (!ref || typeof ref.mergeImage !== "function") continue;
      try {
        const hasStrokes = typeof ref.hasStrokes === "function" && await ref.hasStrokes();
        if (!hasStrokes) continue;
        const merged = await ref.mergeImage();
        if (merged && typeof merged === "string") {
          copy[idx] = { ...note, sessionsImageURL: merged };
          changed = true;
        }
      } catch (e) {
        console.warn("mergeImage failed for index", idx, e);
      }
    }
    if (changed) setNotes(copy);
    return copy;
  };

  // (template section is always mounted so no need to commit on section change)

  // Templates fetch
  useEffect(() => {
    const initializeData = async () => {
      const token = sessionStorage.getItem("user");
      if (!token) return;
      try {
        const decoded = jwtDecode(token);
        const userPractices = decoded.practice;
        if (!userPractices || userPractices.length === 0) return;

        const templatesData = await fetchPracticeTemplates(decoded._id);
        const filteredTemplates = templatesData.filter((template) =>
          template.templatePracticeId.some((p) => userPractices.includes(p)),
        );

        const initialTemplates = filteredTemplates.filter(
          (t) => t.templateInitial === "initial",
        );
        const followUpTemplates = filteredTemplates.filter(
          (t) => t.templateInitial === "followup",
        );

        setDropdownOptions([
          { label: "Initial Assessment", options: initialTemplates },
          { label: "Follow Up", options: followUpTemplates },
        ]);
        setTemplates(filteredTemplates);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    initializeData();
  }, []);

  // ✅ Rehydrate template notes when the session changes (sessionId only —
  // sections/templateId are stable initial data for this session and must not
  // be in the dependency array or they can cause infinite re-renders after save)
  useEffect(() => {
    const normalized = normalizeNotes(sections);
    setNotes(normalized);
    setSelectedTemplateId((prev) => prev || templateId || "");
    initialTemplateIdRef.current = templateId || "";
    annotatorRefs.current = Array(normalized.length).fill(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Notepad content: once per sessionId
  useEffect(() => {
    loadedNotepadRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (loadedNotepadRef.current) return;
    const content =
      safeHtml(notepadContentR) ??
      safeHtml(sections?.notepadContent) ??
      safeHtml(sections?.sessionsNotepadContent) ??
      "";
    setNotepadContent(content);
    loadedNotepadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Handwriting image: sync with prop when sessionId or prop changes
  useEffect(() => {
    setHandwritingImage(handwritingImageR || null);
  }, [sessionId, handwritingImageR]);

  // Detect mobile device for handwriting button visibility
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        window.innerWidth <= 1024 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Template select change
  const handleTemplateChange = (newId) => {
    commitAllImages();
    setSelectedTemplateId(newId);
    const sel = templates.find((t) => t._id === newId);
    if (sel && Array.isArray(sel.notes)) {
      const formatted = sel.notes.map((note) =>
        note.sessionsFieldType === "multiselect"
          ? {
              ...note,
              sessionsSectionNotes: {
                availableOptions: Array.isArray(note.sessionsSectionNotes)
                  ? note.sessionsSectionNotes
                  : [],
                selectedOptions: [],
              },
            }
          : {
              ...note,
              sessionsSectionNotes:
                typeof note.sessionsSectionNotes === "string"
                  ? note.sessionsSectionNotes
                  : "",
            },
      );
      setNotes(formatted);
      annotatorRefs.current = Array(formatted.length).fill(null);
    }
  };

  // Helpers used in render
  const getFieldValue = (note) => {
    if (!note?.sessionsSectionNotes) return "";
    if (typeof note.sessionsSectionNotes === "object") return "";
    return note.sessionsSectionNotes;
  };
  const handleTemplateFieldChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].sessionsSectionNotes = value;
      return updated;
    });
  };
  const handleMultiselectChange = (index, option) => {
    setNotes((prev) =>
      prev.map((note, i) => {
        if (i !== index) return note;
        const current = note.sessionsSectionNotes?.selectedOptions || [];
        const selected = current.includes(option)
          ? current.filter((x) => x !== option)
          : [...current, option];
        return {
          ...note,
          sessionsSectionNotes: {
            ...note.sessionsSectionNotes,
            selectedOptions: selected,
          },
        };
      }),
    );
  };

  // Save
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      // Auto-capture handwriting canvas before saving — only when visible (display:none breaks exportImage)
      let currentHandwriting = handwritingImage;
      if (localSection === "write" && inlineWriteRef.current) {
        const captured = await inlineWriteRef.current.capture();
        if (captured) { currentHandwriting = captured; setHandwritingImage(captured); }
      }

      const mergedNotes = await commitAllImages();

      // process image fields from notes
      const processedNotes = [];
      for (let i = 0; i < (mergedNotes || []).length; i++) {
        const n = mergedNotes[i];
        if (n.sessionsFieldType !== "image") {
          processedNotes.push(n);
          continue;
        }
        const url =
          typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
        if (url && url.startsWith("data:")) {
          const blob = await fetch(url).then((r) => r.blob());
          const fd = new FormData();
          fd.append("image", blob);
          fd.append("x-image-caption", "Session Image Upload");
          fd.append("patientId", patientId);
          fd.append("appointmentId", appointmentId);
          fd.append("type", "session");
          const up = await uploadImage(fd);
          processedNotes.push({ ...n, sessionsImageURL: up.imageUrl });
        } else {
          processedNotes.push(n);
        }
      }

      // media
      const finalMediaFiles = [];
      for (let idx = 0; idx < (mediaImages || []).length; idx++) {
        const m = mediaImages[idx];
        if (typeof m !== "string") continue;
        if (m.startsWith("data:")) {
          const blob = await fetch(m).then((r) => r.blob());
          const fd = new FormData();
          fd.append("image", blob);
          fd.append("x-image-caption", `Media Photo ${idx + 1}`);
          fd.append("patientId", patientId);
          fd.append("appointmentId", appointmentId);
          fd.append("type", "session");
          const up = await uploadImage(fd);
          finalMediaFiles.push({
            fileName: `media-photo-${idx + 1}`,
            fileUrl: up.imageUrl,
            fileType: "image",
            uploadedAt: new Date().toISOString(),
            description: `Captured photo ${idx + 1}`,
          });
        } else if (/^https?:\/\//.test(m)) {
          finalMediaFiles.push({
            fileName: `media-photo-${idx + 1}`,
            fileUrl: m,
            fileType: "image",
            uploadedAt: new Date().toISOString(),
            description: `Captured photo ${idx + 1}`,
          });
        }
      }
      const seen = new Set();
      const mediaFiles = finalMediaFiles.filter((f) => {
        const u = f.fileUrl;
        if (!u || seen.has(u)) return false;
        seen.add(u);
        return true;
      });

      const includeTemplateChange =
        selectedTemplateId &&
        selectedTemplateId !== initialTemplateIdRef.current;

      const payload = {
        sessionsPatientId: patientId,
        sessionsPracticeId: practiceId,
        sessionsAppointmentId: appointmentId,
        sessionsServiceName: serviceName,
        sessionsResourceId: resourceId,
        sessionsAppointmentStart: appointmentStart,
        ...(includeTemplateChange
          ? { sessionsTemplateId: selectedTemplateId }
          : {}),
        // Always send sections to avoid wiping out template on notepad-only edits
        sections: processedNotes,
        ...(notepadContent?.trim()
          ? { notepadContent, notepadContentType: "html" }
          : {}),
        ...(currentHandwriting ? { handwritingImage: currentHandwriting } : {}),
        ...(mediaFiles.length ? { mediaFiles } : {}),
      };

      await updateSessionById(sessionId, payload);

      initialTemplateIdRef.current = selectedTemplateId;
      setNotes(processedNotes);

      stopStream();
      showToast("success", "Session saved successfully!");

      const isUnregistered = String(patientId) === "1";
      setTimeout(() => {
        if (inline) {
          // For inline mode (e.g., PrevSessions), just call the refresh callback
          onSaved?.();
        } else {
          if (isUnregistered) {
            Navigate("/bookings");
          } else {
            onSaved?.();
            Navigate(`/patients/appointment/${patientId}/1`);
          }
        }
      }, 1500);
    } catch (e) {
      console.error("UpdateSession save failed", e);
    } finally {
      setIsSaving(false);
    }
  };

  const renderCurrentSection = () => {
    switch (localSection) {
      case "apptNotepad":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-1">
              <Col md={12}>
                <div className="d-flex align-items-center">
                  <div>
                    <p className="text-muted mb-0">
                      Use this space for quick notes during the session.
                    </p>
                  </div>
                  <div className="d-flex align-items-center ms-auto gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="p-1 border-0"
                      aria-label="Speak to add to notepad"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRecorderForNotepad(); }}
                      title="Speak to add to notepad"
                    >
                      <i className="fa-light fa-microphone fs-6" />
                    </Button>
                    <Button variant="" className="rounded-0 text-primary" onClick={handleExpand} title={isFullScreen ? "Collapse" : "Expand"}>
                      <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="bg-white rounded shadow-sm notepad-container">
                  <ReactQuill
                    key={`quill-${sessionId}`}
                    ref={quillRef}
                    theme="snow"
                    readOnly={false}
                    value={notepadContent}
                    onChange={(html) => setNotepadContent(html)}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link"],
                      ],
                    }}
                    formats={["bold", "italic", "underline", "list", "bullet", "link"]}
                    style={{ minHeight: "200px", height: "300px" }}
                    placeholder="Start typing your notes here..."
                  />
                </div>
              </Col>
            </Row>
          </div>
        );

      case "apptMedia":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-3">
              <Col>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-1">
                <Button variant="" className="rounded-0 text-primary" onClick={handleExpand} title={isFullScreen ? "Collapse" : "Expand"}>
                  <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                </Button>
              </Col>
            </Row>

            {camError ? (
              <Alert variant="warning" className="mb-3">
                Camera access failed ({camError}). You can still upload from gallery:
                <Form.Control
                  className="mt-2"
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((file) => {
                      const reader = new FileReader();
                      reader.onload = () => setMediaImages((prev) => [reader.result, ...prev]);
                      reader.readAsDataURL(file);
                    });
                    e.target.value = "";
                  }}
                />
              </Alert>
            ) : (
              <div className="rounded right-scroll shadow-sm mb-3" style={{ position: "relative" }}>
                <video ref={videoRef} playsInline muted style={{ width: "100%", height: "auto", background: "#000" }} />
                <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
                  <Button variant="light" className="rounded-circle" onClick={capturePhoto} aria-label="Capture">
                    <i className="bi bi-camera-fill" />
                  </Button>
                  <Button variant="light" className="rounded-circle" onClick={flipCamera} aria-label="Flip">
                    <i className="bi bi-arrow-repeat" />
                  </Button>
                </div>
              </div>
            )}

            {mediaImages.length > 0 && (
              <>
                <h6 className="text-primary">Photos ({mediaImages.length})</h6>
                <Row>
                  {mediaImages.map((url, idx) => (
                    <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                      <div className="position-relative border rounded right-scroll">
                        <img src={url} alt={`photo ${idx + 1}`} style={{ width: "100%", height: 100, objectFit: "cover" }} />
                        <Button variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 p-1" onClick={() => removePhoto(idx)} aria-label="Remove" title="Remove">
                          ×
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        );

      default:
        return null; // apptTemplate rendered separately (always mounted)
    }
  };

  const outerStyle = isFullScreen
    ? { position: "fixed", inset: 0, zIndex: 1049, background: "white", overflowY: "auto", display: "flex", flexDirection: "column" }
    : { marginTop: "4px" };

  const sessionContent = (
    <div className="border" style={outerStyle}>
      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />

      <div className={`d-flex flex-grow-1 ${isMobile ? "flex-column" : ""}`}>
        {/* Nav: top row on mobile, left column on desktop */}
        <div
          className={`d-flex gap-1 p-2 ${isMobile ? "flex-row flex-wrap border-bottom" : "flex-column border-end"}`}
          style={isMobile ? {} : { width: "130px", flexShrink: 0 }}
        >
          {navItems.map(({ key, label, icon }) => (
            <Button
              key={key}
              variant=""
              className="rounded-0 d-flex align-items-center gap-2"
              style={{
                backgroundColor: localSection === key ? "#cfe3ff" : "white",
                borderColor: localSection === key ? "#6eaaee" : "#dee2e6",
                color: "#1a3a6b",
                fontWeight: localSection === key ? "600" : "normal",
              }}
              onClick={() => handleSectionChange(key)}
            >
              <i className={icon} />
              {!isMobile && label}
            </Button>
          ))}
          {!isMobile && <hr className="my-2 mx-0" />}
          <Button
            variant="primary"
            className="rounded-0 d-flex align-items-center gap-2"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            <i className="fa-regular fa-floppy-disk" />
            {!isMobile && (isSaving ? "Saving..." : "Save")}
          </Button>
        </div>

        {/* Section content */}
        <div className="p-2 flex-grow-1">
          {/* Write section always mounted so canvas state persists across section switches */}
          <div style={{ display: localSection === "write" ? "block" : "none" }}>
            <InlineWriteSection
              ref={inlineWriteRef}
              onSave={setHandwritingImage}
              existingImage={handwritingImage}
              onExpand={handleExpand}
              isFullScreen={isFullScreen}
            />
          </div>
          {/* Template section always mounted so ImageAnnotator canvas state persists */}
          <div style={{ display: localSection === "apptTemplate" ? "block" : "none" }}>
            <Row className="mb-3 mt-1">
              <Col>
                <Form.Group controlId="formGridSessionTemplate" className="d-flex align-items-center justify-content-center mb-0">
                  <Form.Label className="me-3 mb-0 text-primary fw-bold">Template:</Form.Label>
                  <Form.Select className={isMobile ? "w-75" : "w-25"} value={selectedTemplateId} onChange={(e) => handleTemplateChange(e.target.value)}>
                    <option value="" disabled>Choose a template...</option>
                    {dropdownOptions.map((category, idx) => (
                      <React.Fragment key={idx}>
                        <option disabled className="text-primary fw-bold">{category.label}</option>
                        {category.options.map((template) => (
                          <option key={template._id} value={template._id}>{template.templateTitle}</option>
                        ))}
                      </React.Fragment>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs="auto" className="ms-auto d-flex gap-1 align-items-center">
                <Button variant="" className="rounded-0 text-primary" onClick={handleExpand}>
                  <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                </Button>
              </Col>
            </Row>
            <Row>
              {notes.map((note, idx) => {
                const rowKey = `${sessionId || "session"}-${note.sessionsSection || "sec"}-${idx}`;
                const safeGetFieldValue = (n) => {
                  if (typeof getFieldValue === "function") return getFieldValue(n);
                  if (!n || n.sessionsFieldType === "multiselect") return "";
                  const v = n.sessionsSectionNotes;
                  return typeof v === "string" ? v : (v ?? "") + "";
                };
                const msAvailable = (note.sessionsFieldType === "multiselect" && note.sessionsSectionNotes?.availableOptions) || [];
                const msSelected = (note.sessionsFieldType === "multiselect" && note.sessionsSectionNotes?.selectedOptions) || [];
                return (
                  <Col md={6} key={rowKey} className="mb-3">
                    <div className="border p-3 rounded bg-white">
                      <Form.Label className="text-primary fw-bold d-flex justify-content-between align-items-center">
                        <span className="me-2">{note.sessionsSection || "Field"}</span>
                        {["text", "textarea"].includes(note.sessionsFieldType) && (
                          <Button
                            variant=""
                            size="sm"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRecorderForTemplate(idx); }}
                            title="Speak to fill this field"
                          >
                            <i className="fa-light fa-microphone text-primary" />
                          </Button>
                        )}
                      </Form.Label>
                      {note.sessionsFieldType === "text" && (
                        <Form.Control type="text" value={safeGetFieldValue(note)} onChange={(e) => handleTemplateFieldChange(idx, e.target.value)} />
                      )}
                      {note.sessionsFieldType === "textarea" && (
                        <Form.Control as="textarea" rows={4} value={safeGetFieldValue(note)} onChange={(e) => handleTemplateFieldChange(idx, e.target.value)} />
                      )}
                      {note.sessionsFieldType === "multiselect" && msAvailable.length > 0 && (
                        <div className="mt-2">
                          {msAvailable.map((option, i) => (
                            <Form.Check key={`${rowKey}-opt-${i}`} label={option} type="checkbox" checked={msSelected.includes(option)} onChange={() => handleMultiselectChange(idx, option)} />
                          ))}
                        </div>
                      )}
                      {note.sessionsFieldType === "image" && (
                        <div className="mt-2">
                          <div className="annotator-scroll">
                            <ImageAnnotator
                              key={`${rowKey}-img`}
                              ref={(el) => (annotatorRefs.current[idx] = el)}
                              imageUrl={note.sessionsImageURL}
                              onUpdateImage={(newUrl) => {
                                setNotes((prev) => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], sessionsImageURL: newUrl };
                                  return updated;
                                });
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
          {localSection !== "write" && localSection !== "apptTemplate" && renderCurrentSection()}
        </div>
      </div>

      <RecorderModal
        show={recOpen}
        onClose={closeRecorder}
        onDone={applyTranscriptToTarget}
        vr={vr}
      />
    </div>
  );

  return isFullScreen
    ? createPortal(sessionContent, document.body)
    : sessionContent;
};
